// Assets

import Card from 'components/card';
import SearchSelector from 'components/fields/SearchSelector';
import Switch from 'components/switch';
import { GoDotFill } from 'react-icons/go';
import CreateCategory from './CreateCategory';
import SubCategoryManager from './SubCategoryManager';
import CategoryManager from './CategoryManager';
import SubFamilyManager from './SubFamilyManager';
import { useDisclosure } from '@chakra-ui/hooks';
import { useEffect, useState } from 'react';
import { getMyInfo, updateMyInfo } from 'interfaces/user';

const ProductCommandSettings = (props: { product: any; handleChange: any }) => {
    const { product, handleChange } = props;
    const defaultCategories = [
        {
            name: 'Aucune catégorie',
            id: '',
            _id: '',
        },
    ];

    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenSubCategoryManager,
        onOpen: onOpenSubCategoryManager,
        onClose: onCloseSubCategoryManager,
    } = useDisclosure();
    const {
        isOpen: isOpenCategoryManager,
        onOpen: onOpenCategoryManager,
        onClose: onCloseCategoryManager,
    } = useDisclosure();
    const {
        isOpen: isOpenSubFamilyManager,
        onOpen: onOpenSubFamilyManager,
        onClose: onCloseSubFamilyManager,
    } = useDisclosure();
    const [user, setUser] = useState(null);
    const [userSubCategories, setUserSubCategories] = useState<any[]>([]);
    const [userSubFamilies, setUserSubFamilies] = useState<any[]>([]);

    const initUser = async () => {
        const res = await getMyInfo();
        if (res && res.data) {
            setUser(res.data);
            setUserSubCategories(res.data.subCategories || []);
            setUserSubFamilies(res.data.subFamilies || []);
        }
    };

    const saveSubCategories = async (subCategories: any[]) => {
        setUserSubCategories(subCategories);
        await updateMyInfo({ subCategories });
    };

    const saveSubFamilies = async (subFamilies: any[]) => {
        setUserSubFamilies(subFamilies);
        await updateMyInfo({ subFamilies });
    };

    const saveCategories = async (categories: any[]) => {
        const res = await updateMyInfo({ categories });
        if (res) {
            setUser({
                ...user,
                categories,
            });
        }
    };

    const updateCategories = async (newCategory: any) => {
        console.log(newCategory);
        const newCategoryId = newCategory.name.toLowerCase().replace(' ', '-');
        if (user.categories.find((c: any) => c.id === newCategoryId)) return;

        const res = await updateMyInfo({
            categories: [...user.categories, newCategory],
        });
        if (res) {
            setUser({
                ...user,
                categories: [...user.categories, newCategory],
            });
        }
    };

    const sortAlpha = (array: any) => {
        if (!array) return [];

        return array.sort((a: any, b: any) => a.id.localeCompare(b.id));
    };

    useEffect(() => {
        initUser();
    }, []);

    useEffect(() => {
        if (!product?.productSubCategory) return;
        const selectedSubCat = userSubCategories.find(
            (sc: any) =>
                sc.name === product.productSubCategory && sc.categoryId === product.category,
        );
        const isValid = !!product?.category && !!selectedSubCat;
        if (!isValid) {
            handleChange('productSubCategory', '');
        }
    }, [product?.category, product?.productSubCategory, userSubCategories]);

    useEffect(() => {
        if (!product?.productSubFamily) return;
        const selectedSubCat = userSubCategories.find(
            (sc: any) =>
                sc.name === product.productSubCategory && sc.categoryId === product.category,
        );
        const selectedSubFam = userSubFamilies.find(
            (sf: any) =>
                sf.name === product.productSubFamily &&
                !!selectedSubCat &&
                sf.subCategoryId === selectedSubCat.id,
        );
        const isValid =
            !!product?.category &&
            !!product?.productSubCategory &&
            !!selectedSubCat &&
            !!selectedSubFam;
        if (!isValid) {
            handleChange('productSubFamily', '');
        }
    }, [product?.category, product?.productSubCategory, product?.productSubFamily, userSubFamilies, userSubCategories]);

    return (
        <Card extra="px-5 py-5 border">
            <CreateCategory
                isOpen={isOpen}
                onClose={onClose}
                onClick={(newCategory: any) => updateCategories(newCategory)}
                existingCategories={user?.categories || []}
            />
            <div className="flex justify-between items-center">
                <h4 className="text-lg font-medium">Boutique B2B</h4>
                <div className="flex items-center gap-2">
                    {product?.hiddenFromShop && (
                        <div className="flex gap-1">
                            <p className="text-xs">Produit non vendable</p>
                            <GoDotFill className="text-red-600" />
                        </div>
                    )}
                    {!product?.hiddenFromShop && product?.price > 0 && (
                        <div className="flex gap-[3px]">
                            <p className="text-xs">Produit visible</p>
                            <GoDotFill className="text-green-600" />
                        </div>
                    )}
                    {!product?.hiddenFromShop && product?.price <= 0 && (
                        <div className="flex gap-1">
                            <p className="text-xs">
                                Visible pour les clients avec un prix défini
                            </p>
                            <GoDotFill className="text-toola-500" />
                        </div>
                    )}
                    <Switch
                        id="hiddenFromShop"
                        color="toola"
                        isDefaultChecked={!product?.hiddenFromShop}
                        onChange={(e: any) =>
                            handleChange('hiddenFromShop', !e.target.checked)
                        }
                    />
                </div>
            </div>
            <div>
                <div className="mt-3 col-span-2">
                    <label className="text-navy-700 text-xs font-medium mb-2 block">
                        Catégorie
                    </label>
                    <div className="flex gap-2">
                        <SearchSelector
                            displayKey="name"
                            options={
                                user?.categories
                                    ? [
                                          ...defaultCategories,
                                          ...sortAlpha(user?.categories),
                                      ]
                                    : defaultCategories
                            }
                            value={product?.category}
                            onSelect={(e: any) => {
                                const categoryId = e?.id || e?._id;
                                console.log('Category selected:', categoryId, e);
                                handleChange('category', categoryId);
                            }}
                            defaultKey="id"
                            canCreate={true}
                            createNew={onOpen}
                            inputSmall={true}
                        />
                        <button
                            onClick={onOpenCategoryManager}
                            className="px-3 py-2 bg-toola-500 text-white rounded-xl text-sm hover:bg-toola-600 whitespace-nowrap"
                        >
                            Gérer
                        </button>
                    </div>
                </div>
                <div className="mt-5 col-span-3">
                    <label
                        htmlFor={'description'}
                        className={`text-navy-700 text-xs font-medium`}
                    >
                        Description
                    </label>
                    <textarea
                        id="textInput"
                        className="mt-1 w-full h-[35px] px-4 py-2 border rounded-xl text-sm my-auto focus:outline-none focus:ring focus:border-blue-300 transition-colors duration-300"
                        placeholder="Ajouter ma description ici..."
                        value={product?.description}
                        onChange={(e: any) => {
                            handleChange('description', e.target.value);
                        }}
                    ></textarea>
                </div>
                <div className="mt-5 col-span-3">
                    <label className="text-navy-700 text-xs font-medium mb-2 block">
                        Sous-catégorie du produit
                    </label>
                    <div className="flex gap-2">
                        <select
                            value={product?.productSubCategory || ''}
                            onChange={(e) => handleChange('productSubCategory', e.target.value)}
                            disabled={!product?.category}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                            <option value="">Aucune sous-catégorie</option>
                            {product?.category &&
                                userSubCategories
                                    .filter(
                                        (subCat: any) => subCat.categoryId === product.category,
                                    )
                                    .map((subCat: any) => (
                                        <option key={subCat.id} value={subCat.name}>
                                            {subCat.name}
                                        </option>
                                    ))}
                        </select>
                        <button
                            onClick={onOpenSubCategoryManager}
                            className="px-3 py-2 bg-toola-500 text-white rounded-xl text-sm hover:bg-toola-600 whitespace-nowrap"
                        >
                            Gérer
                        </button>
                    </div>
                </div>
                <div className="mt-5 col-span-3">
                    <label className="text-navy-700 text-xs font-medium mb-2 block">
                        Sous-famille du produit
                    </label>
                    <div className="flex gap-2">
                        <select
                            value={product?.productSubFamily || ''}
                            onChange={(e) => handleChange('productSubFamily', e.target.value)}
                            disabled={!product?.category || !product?.productSubCategory}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                            <option value="">Aucune sous-famille</option>
                            {product?.category &&
                                product?.productSubCategory &&
                                (() => {
                                    const selectedSubCat = userSubCategories.find(
                                        (sc: any) =>
                                            sc.name === product.productSubCategory &&
                                            sc.categoryId === product.category,
                                    );
                                    if (!selectedSubCat) return null;
                                    return userSubFamilies
                                        .filter(
                                            (subFam: any) =>
                                                subFam.subCategoryId === selectedSubCat.id,
                                        )
                                        .map((subFam: any) => (
                                            <option key={subFam.id} value={subFam.name}>
                                                {subFam.name}
                                            </option>
                                        ));
                                })()}
                        </select>
                        <button
                            onClick={onOpenSubFamilyManager}
                            className="px-3 py-2 bg-toola-500 text-white rounded-xl text-sm hover:bg-toola-600 whitespace-nowrap"
                        >
                            Gérer
                        </button>
                    </div>
                </div>
                <SubCategoryManager
                    isOpen={isOpenSubCategoryManager}
                    onClose={onCloseSubCategoryManager}
                    categories={user?.categories || []}
                    subCategories={userSubCategories}
                    onSubCategoriesChange={saveSubCategories}
                />
                <SubFamilyManager
                    isOpen={isOpenSubFamilyManager}
                    onClose={onCloseSubFamilyManager}
                    categories={user?.categories || []}
                    subCategories={userSubCategories}
                    subFamilies={userSubFamilies}
                    onSubFamiliesChange={saveSubFamilies}
                />
                <CategoryManager
                    isOpen={isOpenCategoryManager}
                    onClose={onCloseCategoryManager}
                    categories={user?.categories || []}
                    onCategoriesChange={saveCategories}
                />
            </div>
        </Card>
    );
};

export default ProductCommandSettings;
